import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { pluginApi, serverApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Loader2, Save, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export function WarnsPlugin({ serverId }: { serverId: string }) {
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const { data: channels = [] } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
  });

  const warnsPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'warns');
  const isEnabled = warnsPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) => 
      enabled ? pluginApi.enablePlugin(serverId, 'warns') : pluginApi.disablePlugin(serverId, 'warns'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'warns', config),
    { 
      onSuccess: () => toast.success('Configuration saved!'),
      invalidateQueries: [['servers', serverId, 'plugins']] 
    }
  );

  const [config, setConfig] = useState<any>({
    enabled: true,
    logChannelId: '',
    autoModActions: true,
    warnExpiry: 30,
    actions: [
      { warns: 3, action: 'timeout', duration: 3600 },
      { warns: 5, action: 'kick', duration: 0 },
      { warns: 7, action: 'ban', duration: 0 }
    ]
  });

  useEffect(() => {
    if (warnsPlugin?.config) {
      setConfig({ ...config, ...warnsPlugin.config });
    }
  }, [warnsPlugin]);

  const handleSave = () => {
    updateConfig(config);
  };

  if (!warnsPlugin) return null;

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-orange-500/20 rounded-xl">
            <AlertCircle className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Warning System</h3>
            <p className="text-sm text-muted-foreground">
              Track and manage member warnings with auto-actions.
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={(enabled) => togglePlugin({ enabled })}
        />
      </div>

      {isEnabled && (
        <>
          <div className="glass-card p-6 space-y-4">
            <div>
              <Label>Log Channel</Label>
              <Select
                value={config.logChannelId || 'none'}
                onValueChange={(value) => setConfig({ ...config, logChannelId: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona canale log" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun canale</SelectItem>
                  {(channels as any[]).map((channel: any) => (
                    <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Warning Expiry (days)</Label>
              <Input
                type="number"
                min="0"
                value={config.warnExpiry}
                onChange={(e) => setConfig({ ...config, warnExpiry: parseInt(e.target.value) })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                0 = warnings never expire
              </p>
            </div>

            <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
              <Label className="text-sm">Enable Auto-Mod Actions</Label>
              <Switch
                checked={config.autoModActions}
                onCheckedChange={(checked) => setConfig({ ...config, autoModActions: checked })}
              />
            </div>
          </div>

          <div className="glass-card p-6 space-y-4">
            <h4 className="font-semibold">Available Commands</h4>
            <div className="space-y-2 text-sm">
              <div className="p-3 bg-white/5 rounded-lg">
                <code className="text-orange-400">/warn [@user] [reason]</code>
                <p className="text-muted-foreground mt-1">Issue a warning to a user</p>
              </div>
              <div className="p-3 bg-white/5 rounded-lg">
                <code className="text-orange-400">/warnings [@user]</code>
                <p className="text-muted-foreground mt-1">View user's warning history</p>
              </div>
              <div className="p-3 bg-white/5 rounded-lg">
                <code className="text-orange-400">/clearwarns [@user]</code>
                <p className="text-muted-foreground mt-1">Clear all warnings for a user</p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Configuration
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
